import Check from '@mui/icons-material/Check'
import ContentCopy from '@mui/icons-material/ContentCopy'
import QrCode from '@mui/icons-material/QrCode'
import Share from '@mui/icons-material/Share'
import Visibility from '@mui/icons-material/VisibilityOutlined'
import { Alert, Button, CircularProgress, Stack, TextField } from '@mui/material'
import { Suspense } from 'preact/compat'
import { useEffect, useState } from 'preact/hooks'
import { lazy } from 'preact-iso'
import type { EventDetails } from '../api/generated/EventDetails'
import { shareLink } from './share'

const RegistrationQrDialog = lazy(() =>
  import('./RegistrationQrDialog').then((m) => m.RegistrationQrDialog),
)

/** The registration page's link, to copy, share, print as a QR code, or preview. */
export function RegistrationLink({ event }: { event: EventDetails }) {
  const joinPath = `/events/${event.slug}/join`
  const url = `${window.location.origin}${joinPath}`
  const [copied, setCopied] = useState(false)
  const [showQr, setShowQr] = useState(false)
  const [error, setError] = useState<string>()
  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(timer)
  }, [copied])

  const failed = () =>
    setError("Impossible d'accéder au presse-papiers : copiez le lien à la main.")
  const copy = () => {
    setError(undefined)
    navigator.clipboard.writeText(url).then(() => setCopied(true), failed)
  }
  const share = () => {
    setError(undefined)
    shareLink(navigator, { title: event.name, text: 'Devenez bénévole', url }).then(
      (result) => setCopied(result === 'copied'),
      failed,
    )
  }

  return (
    <Stack spacing={1.5}>
      <TextField
        label="Lien d'inscription"
        size="small"
        fullWidth
        value={url}
        slotProps={{ htmlInput: { readOnly: true } }}
        onFocus={(e) => (e.target as HTMLInputElement).select()}
      />
      {!event.applications_open && (
        <Alert severity="warning">
          Lien inactif tant que les inscriptions sont fermées : les bénévoles qui l'ouvrent lisent
          qu'elles ne sont pas ouvertes.
        </Alert>
      )}
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button
          variant="outlined"
          startIcon={copied ? <Check /> : <ContentCopy />}
          color={copied ? 'success' : 'primary'}
          onClick={copy}
        >
          {copied ? 'Lien copié' : 'Copier'}
        </Button>
        {'share' in navigator && (
          <Button variant="outlined" startIcon={<Share />} onClick={share}>
            Partager
          </Button>
        )}
        <Button variant="outlined" startIcon={<QrCode />} onClick={() => setShowQr(true)}>
          QR code
        </Button>
        <Button startIcon={<Visibility />} href={`${joinPath}/preview`}>
          Aperçu
        </Button>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {showQr && (
        <Suspense fallback={<CircularProgress size={24} />}>
          <RegistrationQrDialog url={url} slug={event.slug} onClose={() => setShowQr(false)} />
        </Suspense>
      )}
    </Stack>
  )
}
